// Escalations filter sidebar.
//
// URL-synced filter controls. The parent EscalationsPage owns the URL state;
// this component renders inputs and calls onChange with a partial patch.

import { Card } from "@/design-system/components/ui";
import type { CriteriaLane, EscalationStatus } from "@/api/dealFlow/types";

const STATUSES: EscalationStatus[] = ["Pending", "Approved", "Denied"];
const LANES: CriteriaLane[] = ["A1", "A2", "A3", "A4"];

interface Props {
  status: EscalationStatus | null;
  lane: CriteriaLane | null;
  from: string;
  to: string;
  onChange: (next: Partial<Record<string, string | null>>) => void;
}

export function EscalationFilterSidebar({
  status, lane, from, to, onChange,
}: Props) {
  return (
    <Card className="p-space-4 flex flex-col gap-space-3">
      <div>
        <div className="text-caption text-gray-mid mb-space-1">Status</div>
        <div className="flex flex-col gap-space-1">
          {STATUSES.map((s) => (
            <label key={s} className="flex items-center gap-space-2">
              <input
                type="radio"
                name="esc-status"
                checked={status === s}
                onChange={() => onChange({ status: s })}
              />
              <span>{s}</span>
            </label>
          ))}
          {/* "all" sentinel, not null — an absent status param now defaults
              to Pending at parse time, so a cleared filter must stay
              explicit in the URL. */}
          <button
            type="button"
            className="text-caption text-gold underline self-start mt-space-1"
            onClick={() => onChange({ status: "all" })}
          >
            Clear status
          </button>
        </div>
      </div>

      <div>
        <div className="text-caption text-gray-mid mb-space-1">Lane</div>
        <div className="flex flex-col gap-space-1">
          {LANES.map((l) => (
            <label key={l} className="flex items-center gap-space-2">
              <input
                type="radio"
                name="esc-lane"
                checked={lane === l}
                onChange={() => onChange({ lane: l })}
              />
              <span>{l}</span>
            </label>
          ))}
          <button
            type="button"
            className="text-caption text-gold underline self-start mt-space-1"
            onClick={() => onChange({ lane: null })}
          >
            Clear lane
          </button>
        </div>
      </div>

      <div>
        <label className="block text-caption text-gray-mid">From</label>
        <input
          type="date"
          value={from}
          onChange={(e) => onChange({ from: e.target.value || null })}
          className="border border-border-light rounded-md px-space-2 py-space-1"
        />
      </div>
      <div>
        <label className="block text-caption text-gray-mid">To</label>
        <input
          type="date"
          value={to}
          onChange={(e) => onChange({ to: e.target.value || null })}
          className="border border-border-light rounded-md px-space-2 py-space-1"
        />
      </div>
    </Card>
  );
}
